import { Controller } from '@hotwired/stimulus';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// Matches the scorecard panel (deals.scss): green actuals, blue targets.
const GREEN = '#7dc142';
const BLUE = '#2a4a9e';
const GREY = '#adb5bd';
const ORANGE = '#fd7e14';

// The deal dashboard's trend charts. `series` is Deals::TrendSeries#chart_rows: one point
// per quarter, oldest first, with nulls for quarters still ahead so the lines stop at today.
export default class extends Controller {
  static values = { series: Array };

  static targets = ['winsChart', 'demosChart', 'ratesChart'];

  connect() {
    this.charts = [];
    this.renderWins();
    this.renderDemos();
    this.renderRates();
  }

  disconnect() {
    this.charts.forEach((chart) => chart.destroy());
    this.charts = [];
  }

  get labels() {
    return this.seriesValue.map((point) => point.label);
  }

  renderWins() {
    if (!this.hasWinsChartTarget) return;
    const points = this.seriesValue;
    this.charts.push(
      new Chart(this.winsChartTarget, {
        data: {
          labels: this.labels,
          datasets: [
            { type: 'bar', label: 'ICP wins', data: points.map((p) => p.icp_wins), backgroundColor: GREEN, order: 2 },
            {
              type: 'line',
              label: 'Target',
              data: points.map((p) => p.target),
              borderColor: BLUE,
              borderDash: [6, 4],
              borderWidth: 2,
              pointRadius: 0,
              spanGaps: false,
              order: 1,
            },
          ],
        },
        options: this.baseOptions({
          y: { beginAtZero: true, ticks: { precision: 0 }, title: { display: true, text: 'Deals' } },
        }),
      })
    );
  }

  renderDemos() {
    if (!this.hasDemosChartTarget) return;
    const points = this.seriesValue;
    this.charts.push(
      new Chart(this.demosChartTarget, {
        type: 'bar',
        data: {
          labels: this.labels,
          datasets: [
            { label: 'ICP demos', data: points.map((p) => p.demos_icp), backgroundColor: BLUE },
            { label: 'Other demos', data: points.map((p) => p.demos_non_icp), backgroundColor: GREY },
          ],
        },
        options: this.baseOptions({
          x: { stacked: true },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: { precision: 0 },
            title: { display: true, text: 'Demos completed' },
          },
        }),
      })
    );
  }

  renderRates() {
    if (!this.hasRatesChartTarget) return;
    const points = this.seriesValue;
    this.charts.push(
      new Chart(this.ratesChartTarget, {
        type: 'line',
        data: {
          labels: this.labels,
          datasets: [
            {
              label: 'ICP close rate',
              data: points.map((p) => p.close_rate_icp),
              borderColor: GREEN,
              backgroundColor: GREEN,
              yAxisID: 'y',
              tension: 0.3,
              spanGaps: false,
            },
            {
              label: 'Close rate, all deals',
              data: points.map((p) => p.close_rate),
              borderColor: BLUE,
              backgroundColor: BLUE,
              yAxisID: 'y',
              tension: 0.3,
              spanGaps: false,
            },
            {
              label: 'Median cycle (days)',
              data: points.map((p) => p.median_cycle_days),
              borderColor: ORANGE,
              backgroundColor: ORANGE,
              borderDash: [4, 4],
              yAxisID: 'y1',
              tension: 0.3,
              spanGaps: false,
            },
          ],
        },
        options: this.baseOptions({
          y: { min: 0, max: 100, title: { display: true, text: 'Close rate %' } },
          y1: {
            position: 'right',
            beginAtZero: true,
            ticks: { precision: 0 },
            grid: { drawOnChartArea: false },
            title: { display: true, text: 'Days' },
          },
        }),
      })
    );
  }

  baseOptions(scales) {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: { legend: { position: 'bottom' } },
      scales,
    };
  }
}
